import { Client } from "@/Common/Api/Client";
import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { MedfarDictionariesPharmacy } from "@/Common/Api/Pharmacy/Dto/Responses/Pharmacy";
import { SearchPharmacyResults } from "@/Common/Api/Pharmacy/Dto/Responses/SearchPharmacyResults";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import * as ServerOperations from "@/Common/Operations/operations.server";

interface IPharmacySearchAutocompleteProps {
    onItemSelectCallback: (item: IPharmacyInfo) => void;
    focusOnMount?: boolean;
    componentId?: string;
    onFocusFunction?: () => void;
    onTextChange: (val: string) => void;
    valid?: boolean;
    customPharmacyBanner?: string;
    customPharmacyFaxNumber?: string;
    maxLength?: number;
}

class PharmacySearchAutocompleteState {
    public results: MedfarDictionariesPharmacy[];
    public displayList: boolean;
    public text: string;

    public constructor(init?: Partial<PharmacySearchAutocompleteState>) {
        Object.assign(this, init);
    }
}

class PharmacySearchAutocomplete extends BaseReactComponent<IPharmacySearchAutocompleteProps, PharmacySearchAutocompleteState> {
    private componentId: string;
    private lockSearch: boolean = false;
    private timeoutReference: number = -1;

    public constructor(props: IPharmacySearchAutocompleteProps) {
        super(props);
        this.state = new PharmacySearchAutocompleteState({
            results: [],
            displayList: false,
            text: this.props.customPharmacyBanner ?? ""
        });

        this.componentId = (this.props.componentId != null && this.props.componentId.trim() !== "")
            ? this.props.componentId
            : GuidHelper.NewGuid();
    }

    public render(): JSX.Element {
        var classname = "form-control";
        if (this.props.valid === false)
            classname += " invalid";

        return (
            <div id={this.componentId} className="pharma-autocomplete">
                <input
                    type="text"
                    className={classname}
                    onKeyDown={this.keyDown.bind(this)}
                    onBlur={this.hideList.bind(this)}
                    onFocus={this.onFocus.bind(this)}
                    onChange={this.onTextChange.bind(this)}
                    value={this.state.text}
                    maxLength={this.props.maxLength}
                />
                {this.state.displayList && (
                    <div className="pharma-results-ps">
                        <div className="pharma-results-container">
                            <ul className="pharma-result-list">
                                {this.state.results.map(r => (
                                    <li onClick={this.selectItem.bind(this, r)} key={this.props.componentId + "_" + r.Id}>
                                        <div className="pharma-result">
                                            <h5>{r.Banner} - {r.Name}</h5>
                                            <p>{r.Address}</p>
                                            <hr />
                                            <p><span>{r.Phone}</span><span className="separator">|</span><span>{r.Fax}</span></p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    public componentDidMount(): void {
        if (this.props.focusOnMount === true)
            $("#" + this.componentId).focus();

        if (!IsNullOrWhiteSpace(this.props.customPharmacyFaxNumber))
            this.fetchPharmacyByFaxnumber();
    }

    public componentDidUpdate(prevProps: Readonly<IPharmacySearchAutocompleteProps>): void {
        if (prevProps.customPharmacyBanner != this.props.customPharmacyBanner && this.props.customPharmacyBanner !== this.state.text) {
            const customState = new PharmacySearchAutocompleteState({ text: this.props.customPharmacyBanner, displayList: false });
            this.setState(customState);
        }
    }

    private onFocus(): void {
        if (this.state.results.length > 0 && !this.state.displayList) {
            var s = new PharmacySearchAutocompleteState();
            s.displayList = true;
            this.setState(s);
        }

        if (this.props.onFocusFunction != null) {
            this.props.onFocusFunction();
        }
    }

    private keyDown(e: React.KeyboardEvent): void {
        if (e.keyCode === 27) {
            var s = new PharmacySearchAutocompleteState();
            s.displayList = false;
            this.setState(s);
        }
    }

    private hideList(): void {
        window.setTimeout(() => {
            var s = new PharmacySearchAutocompleteState();
            s.displayList = false;
            this.setState(s);
        }, 200);
    }

    private onTextChange(e: React.ChangeEvent<HTMLInputElement>): void {
        if (this.lockSearch) { this.lockSearch = false; }
        else {
            var val = e.target.value;
            var s = new PharmacySearchAutocompleteState();
            s.text = val;
            this.setState(s);

            if (this.timeoutReference !== -1) {
                clearTimeout(this.timeoutReference);
                this.timeoutReference = -1;
            }

            var self = this;
            this.timeoutReference = window.setTimeout(() => {
                self.props.onTextChange(val);
                self.executeSearch.apply(self, []);
            }, 400);
        }
    }

    private async executeSearch(customText?: string): Promise<void> {
        const response = await Client.postAsync<SearchPharmacyResults>(
            ServerOperations.Server.MedfarDictionnary_SearchParmacies,
            {
                SearchString: customText ?? this.state.text,
                StateLookUpId: sessionStore.getCurrentEmployeeActiveSiteStateLookUpId()
            }
        );

        if (!response.isSuccess) {
            console.error(response.errors);
            return;
        }

        this.mutateState(s => {
            s.results = [...response.data.SearchPharmacyResults];
            s.displayList = s.results.length > 0;
        });
    }

    private selectItem(item: MedfarDictionariesPharmacy): void {
        this.lockSearch = true;
        this.props.onItemSelectCallback(item);
        var s = new PharmacySearchAutocompleteState();
        s.text = item.Banner;
        s.displayList = false;
        this.setState(s);
    }

    private async fetchPharmacyByFaxnumber(): Promise<void> {
        this.lockSearch = true;

        await this.executeSearch(this.props.customPharmacyFaxNumber);

        if (this.state.results.length > 0) {
            this.props.onItemSelectCallback(this.state.results[0]);

            this.mutateState(s => {
                s.displayList = false;
                s.text = this.state.results[0].Banner;
            });
        }
        else {
            this.mutateState(s => s.text = this.props.customPharmacyBanner);
        }
    }
}

export { PharmacySearchAutocomplete };
